/**
 * Soft-then-hard usage meter with the same 80%/110% thresholds as the
 * server-side EntitlementsService.checkUnderLimit():
 *   - gray when well under limit
 *   - amber at 80% ("you're approaching your cap")
 *   - red at 100% ("upgrade to keep going")
 *
 * Limit semantics:
 *   - `-1` → "Unlimited" badge (no bar).
 *   - `0` with `usage > 0` → "Not on your plan" red bar at 100%. This
 *     matches the entitlements model where free-tier `automationRules`
 *     is `0`: existing rules created before downgrade are still counted
 *     in usage and we want the user to see they're over the cap, not
 *     just a bare "2".
 *   - `0` with `usage === 0` → "Not included" muted text, no bar.
 *   - `>0` → standard usage / limit bar.
 */

interface Props {
  label: string;
  usage: number;
  limit: number; // -1 = unlimited, 0 = not included on this plan
  suffix?: string;
}

export function UsageMeter({ label, usage, limit, suffix }: Props) {
  if (limit === -1) {
    return (
      <div className="space-y-1">
        <div className="flex items-baseline justify-between text-sm">
          <span className="text-gray-600 dark:text-gray-400">{label}</span>
          <span className="text-xs text-emerald-700 font-medium">Unlimited</span>
        </div>
      </div>
    );
  }

  // limit === 0 → feature not included on the current plan. If the
  // tenant has zero usage, render a muted "Not included" message with
  // no bar. If they DO have usage (e.g. they downgraded or the limit
  // was tightened), render the over-cap red bar at 100% so the UI
  // doesn't silently swallow a real plan violation — that's the bug
  // that was hiding the Automation rules count behind a bare "2".
  if (limit === 0) {
    if (usage <= 0) {
      return (
        <div className="space-y-1">
          <div className="flex items-baseline justify-between text-sm">
            <span className="text-gray-600 dark:text-gray-400">{label}</span>
            <span className="text-xs text-gray-500 dark:text-gray-400">Not included</span>
          </div>
        </div>
      );
    }
    const ariaLabel = `${label}: ${usage}${suffix ? ` ${suffix}` : ''} — over plan limit (not included on this plan)`;
    return (
      <div className="space-y-1">
        <div className="flex items-baseline justify-between text-sm">
          <span className="text-gray-600 dark:text-gray-400">{label}</span>
          <span className="text-red-700 font-medium" aria-hidden="true">
            {usage}{suffix ? ` ${suffix}` : ''} <span className="text-xs font-normal">(not on plan)</span>
          </span>
        </div>
        <div
          className="h-1.5 w-full rounded bg-gray-100 dark:bg-gray-700 overflow-hidden"
          role="progressbar"
          aria-label={ariaLabel}
          aria-valuenow={usage}
          aria-valuemin={0}
          aria-valuemax={Math.max(usage, 1)}
        >
          <div className="h-full bg-red-500" style={{ width: '100%' }} />
        </div>
      </div>
    );
  }

  const pct = Math.min(100, Math.round((usage / limit) * 100));
  const level = usage >= limit ? 'red' : usage >= limit * 0.8 ? 'amber' : 'gray';
  const barColor =
    level === 'red'
      ? 'bg-red-500'
      : level === 'amber'
        ? 'bg-amber-500'
        : 'bg-gray-400';

  const statusWord = level === 'red' ? 'at limit' : level === 'amber' ? 'near limit' : 'within limit';
  const ariaLabel = `${label}: ${usage} of ${limit}${suffix ? ` ${suffix}` : ''} used (${pct}% — ${statusWord})`;

  return (
    <div className="space-y-1">
      <div className="flex items-baseline justify-between text-sm">
        <span className="text-gray-600 dark:text-gray-400">{label}</span>
        <span className={level === 'red' ? 'text-red-700 font-medium' : ''} aria-hidden="true">
          {usage} / {limit}{suffix ? ` ${suffix}` : ''}
        </span>
      </div>
      <div
        className="h-1.5 w-full rounded bg-gray-100 dark:bg-gray-700 overflow-hidden"
        role="progressbar"
        aria-label={ariaLabel}
        aria-valuenow={usage}
        aria-valuemin={0}
        aria-valuemax={limit}
      >
        <div
          className={`h-full ${barColor}`}
          style={{ width: `${Math.max(2, pct)}%` }}
        />
      </div>
    </div>
  );
}
